import { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { CrewInformationUb } from "../gql/graphql";
import { useUpsertCrewMutation } from "./useUpsertCrewMutation";
import { useUpdateIsPrivateMutation } from "./useUpdateIsPrivateMutation";
import { CyCrewManagementDialog } from "./CyCrewManagementDialog";
import { v4 as uuidv4 } from "uuid";
import { useHotkeys } from "react-hotkeys-hook";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  bucket: string;
  crew?: CrewInformationUb;
  isPrivateCrew?: boolean;
  isMemberOfCrew?: boolean;
  onSaved?: () => void;
}

export function CrewManagementDialog({
  isOpen,
  toggle,
  bucket,
  crew,
  isPrivateCrew,
  isMemberOfCrew,
  onSaved,
}: Props) {
  const [title, setTitle] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, upsertCrew] = useUpsertCrewMutation();
  const [, updateIsPrivate] = useUpdateIsPrivateMutation();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const isValid = title.trim().length > 0;
  const privateBlockedByMembership =
    crew !== undefined && isMemberOfCrew === false;

  useEffect(() => {
    if (crew) {
      setTitle(crew.title);
      setIsPrivate(isPrivateCrew ?? false);
    } else {
      setTitle("");
      setIsPrivate(false);
    }
  }, [crew, isPrivateCrew, isOpen]);

  const handleSave = async () => {
    if (!isValid || isSaving) return;

    setIsSaving(true);
    const identifier = crew?.identifier ?? uuidv4();
    const joiningIdentifier = crew?.joiningIdentifier ?? uuidv4();
    const result = await upsertCrew({
      identifier,
      title: title.trim(),
      joiningIdentifier,
      bucket,
    });

    if (result.error) {
      setIsSaving(false);
      console.error("Failed to save crew", result.error);
      window.alert(t("crew.manage.saveError"));
      return;
    }

    const isPrivateResult = await updateIsPrivate({
      identifier,
      isPrivateCrew: isPrivate,
      bucket,
    });
    setIsSaving(false);

    if (isPrivateResult.error) {
      console.error("Failed to update crew visibility", isPrivateResult.error);
      const backendMessage = isPrivateResult.error.graphQLErrors[0]?.message;
      window.alert(backendMessage ?? t("crew.manage.saveError"));
      return;
    }

    onSaved?.();
    toggle();
  };

  const handleOpened = () => {
    titleInputRef.current?.focus();
  };

  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (isValid && !isSaving) {
        handleSave();
      }
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      toggle();
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} onOpened={handleOpened}>
      <ModalHeader toggle={toggle}>
        {crew
          ? t("crew.manage.dialog.editTitle", { bucket })
          : t("crew.manage.dialog.createTitle", { bucket })}
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="crewTitle">{t("crew.manage.dialog.nameLabel")}</Label>
            <Input
              innerRef={titleInputRef}
              type="text"
              id="crewTitle"
              data-cy={CyCrewManagementDialog.NameInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("crew.manage.dialog.namePlaceholder")}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("crew.manage.organization")}</Label>
            <p className="text-muted mb-0">{bucket}</p>
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              id="crewIsPrivate"
              data-cy={CyCrewManagementDialog.IsPrivateCheckbox}
              checked={isPrivate}
              disabled={privateBlockedByMembership}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            <Label check for="crewIsPrivate">
              {t("crew.manage.dialog.privateLabel")}
            </Label>
            <p className="text-muted mb-0">
              {privateBlockedByMembership
                ? t("crew.manage.dialog.privateBlockedNotMember")
                : t("crew.manage.dialog.privateHelp")}
            </p>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("common.buttons.cancel")}
        </Button>
        <Button
          color="primary"
          data-cy={CyCrewManagementDialog.SaveButton}
          onClick={handleSave}
          disabled={!isValid || isSaving}
        >
          {isSaving ? t("crew.manage.saving") : t("common.buttons.save")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
